import React, { useContext, useEffect, useRef, useState } from 'react';
import { AppContext } from '../../../contexts/AppContext';
import { toast } from 'react-toastify';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import PurchaseDocumentFormContainer from './PurchaseDocumentFormContainer';
import { PurchaseDocumentServiceManifestDataForm } from '../../../app/models/PurchaseDocument';
import usePurchaseDocumentService from '../../../app/services/hooks/usePurchaseDocumentService';
import { Society } from '../../../app/models/Society';
import { Client } from '../../../app/models/Client';
import useClientService from '../../../app/services/hooks/useClientService';
import useNavigationPage from '../../../hooks/useNavigationPage';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import { Provider } from '../../../app/models/Provider';
import useProviderService from '../../../app/services/hooks/useProviderService';

interface Props {
    onSaved?: (data: any) => void;
    onCancel?: () => void;
    onError?: () => void;
}

const PurchaseDocumentCreate = ({ onSaved, onCancel, onError }: Props) => {
    const { showLoading, hideLoading } = useContext(AppContext);
    const [purchaseDocument, setPurchaseDocument] = useState<any>({});
    const { navigationPage } = useNavigationPage();
    const [societies, setSocieties] = useState<Society[]>([]);
    const [providers, setProviders] = useState<Provider[]>([]);
    const societyIdSelected = useRef<number | undefined>(0);
    const providersFromToi = useRef<Client[]>([]);
    const [paymentStatuses, setPaymentStatuses] = useState<PurchaseDocumentServiceManifestDataForm[]>([]);
    const [errorFields, setErrorFields] = useState<any>();

    const {createPurchaseDocument, fetchingCreatePurchaseDocument} = usePurchaseDocumentService();
    const { storePurchaseDocument, getServiceManifestsByClientSociety ,fetchingGetServiceManifestsByClientSociety } = usePurchaseDocumentService();
    const { getProvidersBySociety, fetchingGetProviderBySociety } = useProviderService();

    useEffect(() => {
        create();
    }, []);

    const create = () => {
        if (showLoading) showLoading('loading', 'Cargando datos...');
        createPurchaseDocument({
            onSuccess: (response: ServiceResponse) => {
                setSocieties(response.data.societies);
                if (hideLoading) hideLoading();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                toast.error(response.message);
                if (onError) onError();
            }
        });
    };

    // Fetch providers when society_id changes
    useEffect(() => {
        if (purchaseDocument.society_id && purchaseDocument.society_id !== societyIdSelected.current) {
            getProvidersBySociety(purchaseDocument.society_id, {
                onSuccess: (response: ServiceResponse) => {
                    setProviders(response.data.providers);
                    societyIdSelected.current = purchaseDocument.society_id;
    
                    // Type prev as PurchaseDocumentType
                    setPurchaseDocument ({
                        ...purchaseDocument,
                        provider_id: 0, // Reset provider selection
                    });
                },
                onError: (response: ServiceResponse) => {
                    toast.error(response.message);
                },
            });
        }
    }, [purchaseDocument.society_id]);


    useEffect(() => {
        if (purchaseDocument.society_id && purchaseDocument.provider_id) {
            console.log("Ejecutando getServiceManifestsByClientSociety con:", purchaseDocument.society_id, purchaseDocument.provider_id);
    
            getServiceManifestsByClientSociety(purchaseDocument.society_id, purchaseDocument.provider_id, {
                onSuccess: (response: ServiceResponse) => {
                    console.log("Respuesta exitosa:", response.data.service_manifests);
                    setPaymentStatuses(response.data.service_manifests);
                },
                onError: (response: ServiceResponse) => {
                    console.error("Error en getServiceManifestsByClientSociety:", response.message);
                    toast.error(response.message);
                }
            });
        }
    }, [purchaseDocument.society_id, purchaseDocument.provider_id]);
    

    const store = () => {
        if (showLoading) showLoading('loading', 'Guardando factura de venta...');
        storePurchaseDocument(purchaseDocument, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                toast.success(response.message, { autoClose: 2500 });
                const purchaseDocumentId = response.data?.tax_document_id;
                if (purchaseDocumentId) {
                    navigationPage(`/tax-documents/${purchaseDocumentId}/edit`); // Redirigir automáticamente
            }
                if (onSaved) {
                    onSaved(response.data);
            }
            
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                useReactConfirmAlert().errorAlert({
                    title: 'Error',
                    message: response.message,
                    message_list: response.data.message_list ?? []
                });
                toast.error(response.message);
            },
            onFieldError: (errorFields: ServiceResponse) => {
                if (hideLoading) hideLoading();
                setErrorFields(errorFields.data);
            }
        });
    };

    const cancel = () => {
        if (onCancel) onCancel();
    };

    return fetchingCreatePurchaseDocument ? (
        <div>Cargando...</div>
    ) : (
        <PurchaseDocumentFormContainer
            action={store}
            cancel={cancel}
            societies={societies}
            providers={providers}
            purchaseDocument={purchaseDocument}
            setPurchaseDocument={setPurchaseDocument}
            availableStatuses={paymentStatuses}
            errorFields={errorFields}
            updated={false}
            disabled={false}
            loadingProvider={fetchingGetProviderBySociety}
            loadingServiceManifestStatus={fetchingGetServiceManifestsByClientSociety}
        />
    );
};

export default PurchaseDocumentCreate;
